import { useEffect, useState } from "react";
import { Modal, Button, Space, message } from "antd";
import { FileTextOutlined, InfoCircleOutlined } from "@ant-design/icons";
import DocumentUpload from "../../../components/DocumentUpload";
import { useDispatch } from "react-redux";
import { actions } from "../../../pages/authenticatedApp/backoffice/OrderDetails/slice";

const getSubtitleFromExtensions = (extensions) => {
  const extensionDisplayNames = {
    pdf: "PDF",
    xlsx: "Excel (XLSX)",
    xls: "Excel (XLS)",
    doc: "Word (DOC)",
    docx: "Word (DOCX)",
  };

  const typeNames = extensions
    .map((ext) => extensionDisplayNames[ext] || ext.toUpperCase())
    .filter(Boolean);
  const uniqueTypes = [...new Set(typeNames)];

  if (uniqueTypes.length === 1) {
    return `${uniqueTypes[0]} document`;
  } else if (uniqueTypes.length <= 3) {
    return `${uniqueTypes.join(", ")} document`;
  } else {
    return "Multiple file types accepted";
  }
};

function UpdateDoc({ orderId, uploadedDocuments, requiredDocuments }) {
  const dispatch = useDispatch();
  const [documents, setDocuments] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const openModal = () => {
    setIsVisible(true);
  };
  const handleClose = () => {
    setIsVisible(false);
    setDocuments(uploadedDocuments || []);
  };

  const handleUpload = (documentName, uploadResult) => {
    const docInfo = requiredDocuments.find(
      (doc) => doc.documentName === documentName
    );

    const fileExtension =
      uploadResult.file.name.split(".").pop()?.toLowerCase() || "";

    const newDocObj = {
      documentName: docInfo?.documentName || "",
      document: uploadResult.url,
      documentType: fileExtension,
      fileName: uploadResult.fileName,
    };

    setDocuments((prevDocs) => {
      const currentDocs = Array.isArray(prevDocs) ? prevDocs : [];

      // Remove existing document with same documentName if it exists
      const filteredDocs = currentDocs.filter(
        (doc) => doc.documentName !== documentName
      );

      return [...filteredDocs, newDocObj];
    });
  };

  const handleRemove = (documentName) => {
    setDocuments((prevDocs) => {
      const currentDocs = Array.isArray(prevDocs) ? prevDocs : [];
      return currentDocs.filter((doc) => doc.documentName !== documentName);
    });
  };

  const handleOk = () => {
    // validation for mandatory documents
    if (!requiredDocuments || !Array.isArray(requiredDocuments)) {
      handleClose();
      return;
    }

    const mandatoryDocs = requiredDocuments.filter(
      (doc) => doc.required === true
    );
    const missingDocs = mandatoryDocs.filter((mandDoc) => {
      return !documents.some(
        (uploadedDoc) => uploadedDoc.documentName === mandDoc.documentName
      );
    });

    if (missingDocs.length > 0) {
      const missingNames = missingDocs.map((doc) => doc.documentName);
      message.error(
        `Please upload mandatory documents: ${missingNames.join(", ")}`
      );
      return;
    }

    setIsLoading(true);
    dispatch(
      actions.updateOrderDocuments({
        orderId,
        data: { documents },
        onSuccess: () => {
          setIsLoading(false);
          setIsVisible(false);
          message.success("Documents updated successfully");
        },
        onFailure: () => {
          setIsLoading(false);
          message.error("Failed to update documents");
        },
      })
    );
  };

  useEffect(() => {
    setDocuments(uploadedDocuments || []);
    console.log({ uploadedDocuments });
  }, [uploadedDocuments]);

  // Transform API documents to component format
  const transformedRequiredDocuments = (requiredDocuments || []).map((doc) => ({
    key: doc._id,
    title: doc.documentName,
    required: doc.required,
    documentType: Array.isArray(doc.documentType)
      ? doc.documentType
      : [doc.documentType],
  }));

  return (
    <>
      <Button type="primary" onClick={openModal} style={{ width: "100%" }}>
        Update Document
      </Button>

      <Modal
        title="Update Document"
        visible={isVisible}
        onOk={handleOk}
        onCancel={handleClose}
        okText="Update"
        cancelText="Cancel"
        width={800}
        confirmLoading={isLoading}
        bodyStyle={{
          maxHeight: "70vh",
          overflowY: "auto",
          overflowX: "auto",
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {/* Header instructions */}
          <div style={{
            background: "linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)",
            borderRadius: 8,
            padding: "16px 20px",
            border: "1px solid #BAE6FD",
            marginBottom: 8
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12
            }}>
              <h4 style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 600,
                color: "#0369A1",
                display: "flex",
                alignItems: "center",
                gap: 8
              }}>
                <FileTextOutlined style={{ fontSize: 18, color: "#0369A1" }} />
                Order Documents
              </h4>
              <div style={{
                background: "#DBEAFE",
                padding: "4px 12px",
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 500,
                color: "#1E40AF"
              }}>
                {documents.length} / {transformedRequiredDocuments.length} uploaded
              </div>
            </div>
            
            <div style={{
              display: "flex",
              gap: 16,
              fontSize: 13,
              color: "#475569"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: "50%", 
                  background: "#EF4444",
                  display: "inline-block"
                }}></span>
                Required
              </div>
              <span style={{ marginLeft: "auto", fontStyle: "italic", display: "flex", alignItems: "center", gap: 6 }}>
                <InfoCircleOutlined style={{ fontSize: 14, color: "#6366F1" }} />
                Upload or replace documents as needed
              </span>
            </div>
          </div>
          
          {transformedRequiredDocuments.map((doc) => {
            const document = Array.isArray(documents)
              ? documents.find(
                  (document) => document.documentName === doc.title
                )
              : null;
            return (
              <DocumentUpload
                key={doc.key}
                title={doc.title}
                subtitle={getSubtitleFromExtensions(doc.documentType)}
                required={doc.required}
                uploadedFile={document}
                onUpload={(uploadResult) =>
                  handleUpload(doc.title, uploadResult)
                }
                onRemove={() => handleRemove(doc.title)}
                documentType={doc.documentType}
              />
            );
          })}
        </Space>
      </Modal>
    </>
  );
}

export default UpdateDoc;
